import BaseCombatant from "./BaseCombatant";
import SoundManager from "../managers/SoundManager";
import GameScene from "../scenes/GameScene";
import CyberShooterAI from "../managers/enemyAI/CyberShooterAI";

export enum CyberShooterActions {
    IDLE = 0,
    MOVING = 1,
    ATTACKING = 2
};

export enum CyberShooterStates {
    NORMAL = 0,
    HIT = 1,
    DEAD = 2
}

export default class CyberShooter extends BaseCombatant {
    currentAction: CyberShooterActions;
    currentState: CyberShooterStates;
    brain: CyberShooterAI | null;
    energyBalls: Phaser.Physics.Arcade.Group;
    shootCooldown: boolean;
    fireDirection: Phaser.Math.Vector2;

    constructor(scene: GameScene, x: number,y: number){
        super(scene,x,y,"CyberShooter");
        this.speed = 10;
        this.maxHealth = 40;
        this.health = this.maxHealth;

        this.name = "cyberShooter";

        const spritesheet = "cyberManSpriteSheet";

        this.scene.anims.create({
            'key': `${this.name}IdleDown`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [0,4,0,4,4,0] }),
            'repeat': -1,
            'delay': 5000,
            'repeatDelay': 7000
        });

        this.scene.anims.create({
            'key': `${this.name}IdleUp`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [4] }),
            'repeat': -1,
            'delay': 5000,
            'repeatDelay': 7000
        });

        this.scene.anims.create({
            'key': `${this.name}MovingDown`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [0,1,2,3] }),
            'repeat': -1
        });

        this.scene.anims.create({
            'key': `${this.name}MovingUp`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [4,5,6,7] }),
            'repeat': -1
        });

        this.scene.anims.create({
            'key': `${this.name}AttackDown`,
            'frameRate': 10,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [9,10,9,10,9,10] }),
            'repeat': 0
        });

        this.scene.anims.create({
            'key': `${this.name}AttackUp`,
            'frameRate': 10,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [5,5,5,5,5,5] }),
            'repeat': 0
        });

        this.scene.anims.create({
            'key': `${this.name}DefendDown`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [11,12,11,12,11,12] }),
            'repeat': -1,
            'delay': 5000,
            'repeatDelay': 7000
        });

        this.scene.anims.create({
            'key': `${this.name}DefendUp`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [12] }),
            'repeat': -1
        });

        this.scene.anims.create({
            'key': `${this.name}Dead`,
            'frameRate': 20,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [12,13,14,15,16,17,18,19,20,21,22,23,24,25] }),
            'repeat': 0
        });

        this.currentAction = CyberShooterActions.IDLE;

        this.setState(CyberShooterStates.NORMAL);

        this.brain = null;
        this.energyBalls = scene.energyBalls;
        this.fireDirection = new Phaser.Math.Vector2(0,0);

    }

    move(direction: Phaser.Math.Vector2){
        if(
            (direction.x === this.prevDirection.x && direction.y === this.prevDirection.y) ||
            this.currentAction === CyberShooterActions.ATTACKING ||
            this.currentState === CyberShooterStates.HIT ||
            this.currentState === CyberShooterStates.DEAD
        ) return;
        if(direction.x === 1 || direction.x === -1){
            this.directionFacing.x = direction.x;
        }
        if(direction.y === 1 || direction.y === -1){
            this.directionFacing.y = direction.y;
        }
        if(direction.y === 0 && direction.x === 0){
            this.setAction(CyberShooterActions.IDLE);
        }else{
            this.setAction(CyberShooterActions.MOVING);
        }
        this.prevDirection = direction;
        this.setAcceleration(direction.x * this.speed * 5, direction.y * this.speed * 5);
    }

    setState(newState: CyberShooterStates){
        switch(newState){
            case CyberShooterStates.NORMAL:
                this.stop();
                this.setAlpha(1);
                this.currentState = newState;
                this.setAction(CyberShooterActions.IDLE);
                this.setFriction(this.speed * 500);
                this.setDrag(this.speed * 5);
                this.setMaxVelocity(this.speed,this.speed);
                this.shadow.setVisible(true);
                this.body.enable = true;
            break;
            case CyberShooterStates.HIT:
                this.setAlpha(0.5);
                this.setDrag(1);
                this.anims.play( this.directionFacing.y === -1 ? `${this.name}DefendUp` : `${this.name}DefendDown`);
                this.scene.time.delayedCall(500, ()=> { this.setState(CyberShooterStates.NORMAL) });
            break;
            case CyberShooterStates.DEAD:
                SoundManager.playSfx('death');
                this.anims.play(`${this.name}Dead`);
                this.shadow.setVisible(false);
                this.body.enable = false;
            break;
        }
        this.currentState = newState;
        return this;
    }

    setAction(newAction: CyberShooterActions){
        if(this.currentState === CyberShooterStates.HIT || this.currentState === CyberShooterStates.DEAD) return;
        switch(newAction){
            case CyberShooterActions.IDLE:
                this.prevDirection.x = 0;
                this.prevDirection.y = 0;
                if(this.directionFacing.y === -1)
                    this.anims.play(`${this.name}IdleUp`)
                if(this.directionFacing.y === 1)
                    this.anims.play(`${this.name}IdleDown`)
                if(this.directionFacing.x === -1)
                    this.setFlipX(true);
                if(this.directionFacing.x === 1)
                    this.setFlipX(false);
                this.currentAction = newAction;
            break;
            case CyberShooterActions.MOVING:
                if(this.directionFacing.y === -1)
                    this.anims.play(`${this.name}MovingUp`)
                if(this.directionFacing.y === 1)
                    this.anims.play(`${this.name}MovingDown`)
                if(this.directionFacing.x === -1)
                    this.setFlipX(true);
                if(this.directionFacing.x === 1)
                    this.setFlipX(false);
                this.currentAction = newAction;
            break;
            case CyberShooterActions.ATTACKING:
                this.stop();
                this.shootCooldown = false;
                const cb = () => {
                    this.scene.time.delayedCall(6000, ()=> this.shootCooldown = true );
                    this.energyBalls.get(this.x,this.y).fire(this.fireDirection);
                    this.setAction(CyberShooterActions.IDLE)
                }
                if(this.directionFacing.y === -1)
                    this.anims.play(`${this.name}AttackUp`).once('animationcomplete', cb);
                if(this.directionFacing.y === 1)
                    this.anims.play(`${this.name}AttackDown`).once('animationcomplete', cb);
                this.currentAction = newAction;
            break;
        }
    }

    takeHit(direction: Phaser.Math.Vector2,damage: number = 10){
        this.health -= damage;
        if(this.health <= 0){
            this.stop();
            this.health = 0;
            this.setState(CyberShooterStates.DEAD);
            return;
        }
        this.setAction(CyberShooterActions.IDLE);
        const randomize = new Phaser.Math.Vector2( (50 * Math.random()) - 25, (50 * Math.random()) - 25 );
        this.setVelocity((direction.x * this.speed * 4) + randomize.x,(direction.y * this.speed * 4) + randomize.y);
        this.setMaxVelocity(this.speed * 5,this.speed * 5);
        this.setState(CyberShooterStates.HIT);
    }

    isAttacking(){
        return this.currentAction === CyberShooterActions.ATTACKING;
    }

    canBeHit(){
        return (
        this.currentState === CyberShooterStates.NORMAL
        )
    }

    canShoot(){
        return this.shootCooldown;
    }

    reset(){
        super.reset();
        this.setState(CyberShooterStates.NORMAL);
        this.shootCooldown = true;
        if(this.brain !== null) this.brain.reset();
    }

    update(time: number, delta: number){
        if(this.brain !== null) this.brain.update(time, delta);
    }

}